import React, { useEffect } from 'react';
import { CheckCircle, AlertCircle, X } from 'lucide-react';
import { useWindows } from '../contexts/WindowsContext';

interface NotificationProps {
  type: 'success' | 'error' | 'info';
  message: string;
  actionText?: string;
  onAction?: () => void;
  onClose: () => void;
}

export const Notification: React.FC<NotificationProps> = ({ type, message, actionText, onAction, onClose }) => {
  const { isMobile } = useWindows();

  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 5000);

    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed z-50 animate-slide-in-right ${isMobile ? 'bottom-20 left-4 right-4' : 'top-4 right-4'}`} role="alert">
      <div className={`flex items-center p-4 rounded-lg shadow-lg max-w-md ${
        type === 'success' 
          ? 'bg-green-50 border border-green-200 text-green-800' 
          : type === 'error' 
            ? 'bg-red-50 border border-red-200 text-red-800'
            : 'bg-yellow-50 border border-yellow-200 text-yellow-800'
      }`}>
        {type === 'success' ? (
          <CheckCircle className="w-5 h-5 mr-3 text-green-600" />
        ) : type === 'error' ? (
          <AlertCircle className="w-5 h-5 mr-3 text-red-600" />
        ) : (
          <svg className="w-5 h-5 mr-3 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )}
        <div className="flex-1">
          <p className="text-sm font-medium">{message}</p>
          {actionText && onAction && (
            <button
              onClick={() => {
                onAction();
                onClose();
              }}
              className="text-xs underline mt-1 hover:no-underline transition-all"
            >
              {actionText}
            </button>
          )}
        </div>
        <button
          onClick={onClose}
          className="ml-3 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}; 